import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { LocalizedLink, useBarePathname } from "../i18n/LocaleRouting";

type NavItem = {
  id: number;
  titleKey: string;
  path: string;
  match: (barePathname: string) => boolean;
  icon: React.FC<{ active: boolean }>;
};

const iconClass = (active: boolean) =>
  active ? "text-[#F05A2A]" : "text-gray-300";

const HomeNavIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    className={iconClass(active)}
    aria-hidden
  >
    <path
      d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const NewsNavIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    className={iconClass(active)}
    aria-hidden
  >
    <path
      d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M18 8h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M8 8h6M8 12h6M8 16h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const MapNavIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    className={iconClass(active)}
    aria-hidden
  >
    <path
      d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const CommunityNavIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    className={iconClass(active)}
    aria-hidden
  >
    <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M3.5 18.5c.8-2.8 3-4.5 5.5-4.5s4.7 1.7 5.5 4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14 14c1.5-.7 3.2-.6 4.6.4 1.2.9 2 2.3 2.3 4.1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

function isExactOrPrefix(pathname: string, base: string) {
  return pathname === base || pathname.startsWith(`${base}/`);
}

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const barePathname = useBarePathname();
  const labels = useMemo<NavItem[]>(
    () => [
      {
        id: 1,
        titleKey: "nav.home",
        path: "/",
        match: (pathname) => pathname === "/",
        icon: HomeNavIcon,
      },
      {
        id: 2,
        titleKey: "nav.news",
        path: "/news",
        match: (pathname) => isExactOrPrefix(pathname, "/news"),
        icon: NewsNavIcon,
      },
      {
        id: 3,
        titleKey: "nav.map",
        path: "/regionalstore",
        match: (pathname) => isExactOrPrefix(pathname, "/regionalstore"),
        icon: MapNavIcon,
      },
      {
        id: 4,
        titleKey: "nav.community",
        path: "/community",
        match: (pathname) => isExactOrPrefix(pathname, "/community"),
        icon: CommunityNavIcon,
      },
    ],
    [],
  );

  return (
    <>
      <div className="hidden md:flex h-[55px] items-center justify-center">
        <ul className="flex w-full items-center justify-between gap-x-5 font-medium text-gray-300">
          {labels.map((label) => {
            const active = label.match(barePathname);
            return (
              <LocalizedLink key={label.id} to={label.path}>
                <li
                  className={`flex items-center justify-center ${
                    active
                      ? "font-bold text-[#F05A2A]"
                      : "hover:font-bold hover:text-[#F05A2A]"
                  }`}
                >
                  <span className="text-sm">{t(label.titleKey)}</span>
                </li>
              </LocalizedLink>
            );
          })}
        </ul>
      </div>

      {typeof document !== "undefined"
        ? createPortal(
            <nav
              className="fixed bottom-0 left-0 right-0 z-[2147483648] border-t border-white/10 bg-black md:hidden"
              style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
              aria-label="Main"
            >
              <ul className="mx-auto flex h-14 max-w-[1600px] items-stretch justify-around px-2">
                {labels.map((label) => {
                  const active = label.match(barePathname);
                  const Icon = label.icon;
                  return (
                    <li key={label.id} className="flex-1">
                      <LocalizedLink
                        to={label.path}
                        className={`flex h-full flex-col items-center justify-center gap-0.5 text-[11px] ${
                          active
                            ? "font-bold text-[#F05A2A]"
                            : "font-medium text-gray-300"
                        }`}
                      >
                        <Icon active={active} />
                        <span>{t(label.titleKey)}</span>
                      </LocalizedLink>
                    </li>
                  );
                })}
              </ul>
            </nav>,
            document.body,
          )
        : null}
    </>
  );
};

export default Navbar;
